/**
 * The schema cache, flattened for the authoring surface (`SC-F411`, `SC-S507`).
 *
 * The cache is owned by `internal/datasources` and read through
 * `api/datasources.ts`'s `getSchema`. This module is the policy editors' adapter
 * over it, and it exists for two reasons rather than as a convenience wrapper.
 *
 * First, the wire shape is **one JSON tree per catalog** — a storage decision, so
 * that a refresh is not a delete-and-reinsert of tens of thousands of rows through
 * the single control-plane write connection — and every consumer here wants the
 * flat, fully-qualified form a pattern preview matches against. The walk is done
 * once, here.
 *
 * Second, and the reason the walk is written structurally rather than against an
 * imported response type: **a missing or stale cache must never block authoring.**
 * `SC-S407`'s fourth criterion is that a completion field degrades to free text
 * with a warning, and the route already honours that by answering `200` with
 * `status.degraded` instead of a 404. A transport failure has to degrade the same
 * way, so [loadSchemaCache] resolves rather than rejects and the caller renders a
 * preview that says "unknown" instead of a page that says "error".
 */

import { getSchema } from "../../api/datasources.js";

/** PreviewTable is one fully-qualified table, with its column names only —
 * a preview matches names and never renders a type. */
export interface PreviewTable {
  catalog: string;
  schema: string;
  table: string;
  columns: string[];
}

/**
 * SchemaCache is what a pattern preview reads.
 *
 * `degraded` is the single bit that decides whether a preview may say "matches
 * nothing". With a degraded cache there is no evidence for that claim, and
 * "matches nothing" is an alarming statement to make without evidence — so the
 * preview says the answer is unknown instead.
 */
export interface SchemaCache {
  tables: PreviewTable[];
  degraded: boolean;
  /** The cache's own sentence about its freshness or its absence, shown verbatim. */
  warning: string;
  /** When the catalogs were read, RFC 3339, or empty. The preview quotes it
   * because it reflects the schema at that moment and a later schema change can
   * alter the outcome. */
  fetchedAt: string;
}

/** EMPTY_CACHE is the honest unknown. */
export const EMPTY_CACHE: SchemaCache = {
  tables: [],
  degraded: true,
  warning: "",
  fetchedAt: "",
};

/**
 * loadSchemaCache reads and flattens one instance's schema cache.
 *
 * It never rejects. A failure — the route is unreachable, the caller is a viewer
 * on an instance whose datasources they cannot read — produces a degraded cache
 * carrying the reason, because the alternative is an editor that refuses to open
 * because its autocomplete could not load.
 */
export async function loadSchemaCache(instanceId: string): Promise<SchemaCache> {
  try {
    return flatten(await getSchema(instanceId));
  } catch {
    return {
      tables: [],
      degraded: true,
      warning:
        "The schema cache could not be read, so pattern previews are unavailable. Authoring is not blocked.",
      fetchedAt: "",
    };
  }
}

/**
 * flatten walks the response structurally.
 *
 * Every level is narrowed rather than asserted. The tree is a hand-shaped document
 * that a truncating writer may leave partial — `Tree.truncated` exists precisely
 * because it does — and a preview that throws on a missing `columns` array would
 * turn an incomplete cache into a broken editor.
 */
function flatten(response: unknown): SchemaCache {
  const root = record(response);
  const catalogs = array(root["catalogs"]);
  const tables: PreviewTable[] = [];
  for (const raw of catalogs) {
    const entry = record(raw);
    const catalog = text(entry["catalog"]);
    const tree = record(entry["tree"]);
    for (const rawSchema of array(tree["schemas"])) {
      const schemaNode = record(rawSchema);
      const schema = text(schemaNode["name"]);
      for (const rawTable of array(schemaNode["tables"])) {
        const tableNode = record(rawTable);
        tables.push({
          catalog,
          schema,
          table: text(tableNode["name"]),
          columns: array(tableNode["columns"])
            .map((c) => text(record(c)["name"]))
            .filter((n) => n !== ""),
        });
      }
    }
  }
  const status = record(root["status"]);
  return {
    tables,
    // An empty tree is degraded whatever the status says: there is nothing to
    // resolve a pattern against, so a preview has no evidence either way.
    degraded: tables.length === 0 || status["degraded"] === true,
    warning: text(status["warning"]),
    fetchedAt: text(status["fetched_at"]),
  };
}

function record(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function array(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function text(value: unknown): string {
  return typeof value === "string" ? value : "";
}
